/**
 * Generate one tracking link per recipient.
 *
 * The clients come from the workspace's own loaded book, so this works the
 * same whether the accounts are kept here or in the connected clients app —
 * the server resolves whichever it is and stores the id opaquely.
 *
 * The thing this screen has to be honest about is what it does and does not
 * do: it makes links. It does not send them. There is no delivery here, no
 * open, no read receipt — a personalised link tells you that this person
 * clicked, and nothing about whether the message reached them. That is stated
 * on the panel rather than left for someone to assume.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { changed, copy } from '../../lib/urltracking.js';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group } from '../Sheet.jsx';
import { Spinner } from '../Primitives.jsx';
import { num } from '../../pages/urltracking/TrackBits.jsx';

const MAX = 500;

export default function TrackPersonalise({ id, onClose }) {
  const { rows, toast } = useApp();
  const [link, setLink] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.urlTracking.links.get(id).then(setLink).catch(e => toast(e.message));
  }, [id, toast]);

  /* The client book as already loaded by the app — no second fetch, and no
     second idea of who the clients are. */
  const clients = useMemo(() => (rows || [])
    .filter(c => c && c.id && !c.archived)
    .map(c => ({ id: String(c.id), name: c.name || String(c.id), email: c.email || '' })), [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? clients.filter(c => c.name.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle))
      : clients;
    return list.slice(0, 400);
  }, [clients, q]);

  const toggle = clientId => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(clientId)) next.delete(clientId);
    else next.add(clientId);
    return next;
  });

  const pickAllShown = () => setPicked(prev => {
    const next = new Set(prev);
    /* Only what is on screen, and never past the server's ceiling — silently
       selecting more than can be generated would fail at the last step. */
    for (const c of shown) { if (next.size >= MAX) break; next.add(c.id); }
    return next;
  });

  const generate = async () => {
    if (!picked.size) { toast('Choose at least one recipient'); return; }
    setBusy(true);
    try {
      const out = await api.urlTracking.links.personalise(id, [...picked]);
      setResult(out);
      setPicked(new Set());
      changed();
      toast(out.summary);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    const text = result.made
      .map(l => `${l.client_name}\t${l.short_url}`)
      .join('\n');
    const ok = await copy(text);
    toast(ok ? 'Copied as two columns — paste straight into a sheet' : 'Could not copy');
  };

  return (
    <Sheet
      icon="users"
      tone="g-blue"
      title="Personalised tracking links"
      onClose={onClose}
      closeLabel="Done"
      wide
      onSave={result ? undefined : generate}
      saveLabel={busy ? 'Generating…' : `Generate ${picked.size || ''} link${picked.size === 1 ? '' : 's'}`.trim()}
    >
      {!link ? <p className="muted">Loading…</p> : (
        <>
          <p className="utnote">
            Each recipient gets their own short code pointing at the same destination with the same
            UTM tags as <b>{link.name || link.short_code}</b>. A click on one of these is known to be
            that person&apos;s — which a shared campaign link can never tell you.
          </p>

          {result ? (
            <>
              <Group>{result.summary}</Group>

              {result.made.length ? (
                <div className="f wide">
                  <div className="utfilter" style={{ marginBottom: 10 }}>
                    <button type="button" className="btn" onClick={copyAll}>
                      <Icon name="file" />Copy all as a table
                    </button>
                    <span className="dim">Name and link, tab separated — paste into a spreadsheet or a mail merge.</span>
                  </div>

                  <div className="tscroll" style={{ maxHeight: 320 }}>
                    <table>
                      <thead><tr><th>Recipient</th><th>Short URL</th></tr></thead>
                      <tbody>
                        {result.made.map(l => (
                          <tr key={l.id}>
                            <td><b>{l.client_name}</b></td>
                            <td><code>{l.short_url}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {result.skipped.length ? (
                <p className="utnote">
                  Already had a link for this message, so they were left alone rather than given a
                  second one: {result.skipped.slice(0, 12).join(', ')}
                  {result.skipped.length > 12 ? ` and ${result.skipped.length - 12} more` : ''}.
                </p>
              ) : null}

              {result.unknown.length ? (
                <p className="utbad">
                  {result.unknown.length} id{result.unknown.length === 1 ? '' : 's'} could not be
                  matched to a client and were skipped.
                </p>
              ) : null}

              <div className="utfilter" style={{ marginTop: 12 }}>
                <button type="button" className="btn" onClick={() => setResult(null)}>
                  <Icon name="plus" />Generate for more recipients
                </button>
              </div>
            </>
          ) : (
            <>
              <Group>
                Recipients
                {picked.size ? ` — ${num(picked.size)} chosen` : ''}
                {picked.size >= MAX ? ` (${MAX} is the most in one go)` : ''}
              </Group>

              <div className="f wide">
                <div className="utfilter" style={{ marginBottom: 10 }}>
                  <div className="f grow">
                    <label htmlFor="utp-q">Search</label>
                    <input
                      id="utp-q"
                      value={q}
                      placeholder="Client name or email…"
                      onChange={e => setQ(e.target.value)}
                    />
                  </div>
                  <button type="button" className="btn" onClick={pickAllShown} disabled={!shown.length}>
                    <Icon name="check" />Select these {shown.length}
                  </button>
                  <button type="button" className="btn" onClick={() => setPicked(new Set())} disabled={!picked.size}>
                    <Icon name="x" />Clear
                  </button>
                </div>

                {clients.length ? (
                  <div className="utpicklist">
                    {shown.map(c => (
                      <label key={c.id} className={picked.has(c.id) ? 'on' : undefined}>
                        <input
                          type="checkbox"
                          checked={picked.has(c.id)}
                          onChange={() => toggle(c.id)}
                        />
                        <span>
                          <b>{c.name}</b>
                          {c.email ? <small>{c.email}</small> : null}
                        </span>
                      </label>
                    ))}
                    {clients.length > shown.length ? (
                      <p className="muted">
                        Showing {shown.length} of {num(clients.length)} — search to narrow it down.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="muted">This workspace has no clients to generate links for yet.</p>
                )}
              </div>
            </>
          )}

          <p className="utnote">
            This generates links. It does not send them — nothing here can tell you whether a message
            arrived or was read, only whether the link in it was clicked. The recipient&apos;s identity
            is never in the URL: each code is random, and who it belongs to is kept here, behind this
            workspace&apos;s permissions.
          </p>

          {busy ? <p className="muted"><Spinner /> Generating…</p> : null}
        </>
      )}
    </Sheet>
  );
}
